import React, { useState, useEffect, useRef } from 'react';
import { Search, User, X } from 'lucide-react';
import { api } from '../services/api';

interface Militar {
  id: string;
  nick: string;
  patente?: {
    patente: string;
    categoria: string;
  };
  nivel_patente?: number;
}

interface OficiaisSelectFieldProps {
  value?: string;
  onChange: (militarId: string, militarNick: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  label?: string;
  required?: boolean;
}

export function OficiaisSelectField({
  value = '',
  onChange,
  placeholder = 'Digite o nome do oficial...',
  disabled = false,
  error,
  label,
  required = false
}: OficiaisSelectFieldProps) {
  const [militares, setMilitares] = useState<Militar[]>([]);
  const [filteredMilitares, setFilteredMilitares] = useState<Militar[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMilitar, setSelectedMilitar] = useState<Militar | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Carregar oficiais na montagem do componente
  useEffect(() => {
    loadOficiais();
  }, []);

  // Encontrar militar selecionado baseado no value
  useEffect(() => {
    if (!Array.isArray(militares)) return;

    if (value && militares.length > 0) {
      const militar = militares.find(m => m.id === value);
      if (militar) {
        setSelectedMilitar(militar);
        setSearchTerm(militar.nick);
      }
    } else {
      setSelectedMilitar(null);
      setSearchTerm('');
    }
  }, [value, militares]);

  // Filtrar oficiais baseado no termo de busca
  useEffect(() => {
    if (!Array.isArray(militares)) return;
    if (!searchTerm.trim()) {
      setFilteredMilitares(militares.slice(0, 10));
    }
  }, [militares]);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadOficiais = async () => {
    setLoading(true);
    try {
      const response = await api.get('/militares/listOficiais');
      const data = response.data;

      let oficiais = data;
      if (!Array.isArray(data) && data.militares) {
        oficiais = data.militares;
      }

      setMilitares(Array.isArray(oficiais) ? oficiais : []);
    } catch (error) {
      console.error('Erro ao carregar oficiais:', error);
      setMilitares([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!newValue.trim()) {
      setSelectedMilitar(null);
      onChange('', '');
      setFilteredMilitares(militares.slice(0, 10));
      setSearching(false);
      return;
    }

    setSearching(true);

    debounceRef.current = setTimeout(async () => {
      try {
        const response = await api.get(`/militares/listOficiais?nick=${encodeURIComponent(newValue)}`);
        const data = response.data;
        setFilteredMilitares(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Erro ao buscar oficiais:', err);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const handleSelectMilitar = (militar: Militar) => {
    setSelectedMilitar(militar);
    setSearchTerm(militar.nick);
    setIsOpen(false);
    onChange(militar.id, militar.nick);
  };

  const handleClearSelection = () => {
    setSelectedMilitar(null);
    setSearchTerm('');
    setIsOpen(false);
    onChange('', '');
    inputRef.current?.focus();
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-sm font-medium text-muted-foreground">
          {label}
          {required && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            disabled={disabled || loading}
            className={`
              w-full px-3 py-2 pr-10 border border-border rounded-md shadow-sm focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground
              ${error ? 'border-red-300 dark:border-red-800 focus:border-red-500 focus:ring-red-500' : ''}
              ${disabled ? 'bg-muted cursor-not-allowed' : ''}
              ${loading ? 'cursor-wait' : ''}
            `}
          />

          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {selectedMilitar ? (
              <button
                type="button"
                onClick={handleClearSelection}
                disabled={disabled}
                className="text-muted-foreground hover:text-foreground focus:outline-none"
              >
                <X size={16} />
              </button>
            ) : (
              <Search size={16} className="text-muted-foreground" />
            )}
          </div>
        </div>

        {/* Dropdown com oficiais */}
        {isOpen && !disabled && (
          <div
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-60 overflow-y-auto"
          >
            {loading ? (
              <div className="px-3 py-2 text-center text-muted-foreground">
                Carregando oficiais...
              </div>
            ) : searching ? (
              <div className="px-3 py-2 text-center text-muted-foreground">
                Buscando...
              </div>
            ) : filteredMilitares.length > 0 ? (
              filteredMilitares.map((militar) => (
                <button
                  key={militar.id}
                  type="button"
                  onClick={() => handleSelectMilitar(militar)}
                  className="w-full px-3 py-2 text-left hover:bg-muted focus:bg-muted focus:outline-none flex items-center space-x-2"
                >
                  <User size={16} className="text-blue-600" />
                  <div>
                    <span className="font-medium text-foreground">{militar.nick}</span>
                    {militar.patente && (
                      <span className="ml-2 text-sm text-blue-600">
                        {typeof militar.patente === 'string' ? militar.patente : militar.patente.patente}
                      </span>
                    )}
                  </div>
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-center text-muted-foreground">
                {searchTerm.trim() ? 'Nenhum oficial encontrado' : 'Digite para buscar oficiais'}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
}
